import i18n from '../../../i18n';
import type {
  FtueJourney,
  FtueJourneyId,
  FtueMode,
  FtueSurfaceContext,
  FtueSurfaceId,
  FtueTarget,
  FtueTourStep,
} from './types';

function tList(key: string): string[] {
  const value = i18n.t(key, { returnObjects: true });
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

export function resolveFtueJourneyLabel(id: FtueJourneyId): string {
  switch (id) {
    case 'full':
      return i18n.t('ftue:journeys.full');
    case 'surface.live':
    case 'surface.performance':
    case 'surface.settings':
    case 'surface.horizon':
      return i18n.t('ftue:journeys.surfaceTour');
    case 'surface.comparison.live':
    case 'surface.comparison.performance':
    case 'surface.comparison.settings':
      return i18n.t('ftue:journeys.comparisonTour');
    case 'deep.live.zones':
      return i18n.t('ftue:journeys.deepLive');
    case 'deep.perf.zones':
      return i18n.t('ftue:journeys.deepPerf');
    case 'deep.perf.drill':
      return i18n.t('ftue:journeys.deepEvidence');
    case 'deep.settings.zones':
      return i18n.t('ftue:journeys.deepSettings');
  }
}

export function resolveFtueSurfaceLabel(surface: FtueSurfaceId): string {
  return i18n.t(`ftue:surfaces.${surface}`);
}

/** Resolve translatable fields at read time (never cache at module init). */
export function resolveFtueTarget(target: FtueTarget): FtueTarget {
  const key = `ftue:targets.${target.id}`;
  return {
    ...target,
    title: i18n.t(`${key}.title`),
    body: i18n.t(`${key}.body`),
    keywords: tList(`${key}.keywords`),
    actions: tList(`${key}.actions`),
    catalogNote:
      target.catalogNote != null || i18n.exists(`${key}.catalogNote`)
        ? i18n.t(`${key}.catalogNote`)
        : undefined,
  };
}

function withLocalizedCopy(target: FtueTarget): FtueTarget {
  const key = `ftue:targets.${target.id}`;
  return {
    ...target,
    get title() {
      return i18n.t(`${key}.title`);
    },
    get body() {
      return i18n.t(`${key}.body`);
    },
    get keywords() {
      return tList(`${key}.keywords`);
    },
    get actions() {
      return tList(`${key}.actions`);
    },
    get catalogNote() {
      return target.catalogNote != null || i18n.exists(`${key}.catalogNote`)
        ? i18n.t(`${key}.catalogNote`)
        : undefined;
    },
  };
}

/** Stable data-ftue anchor ids used across the shell. */
export const FTUE_ANCHORS = {
  navModes: 'nav-modes',
  navHelp: 'nav-help',
  navSources: 'nav-sources',
  scope: 'chrome-scope',
  period: 'chrome-period',
  status: 'chrome-status',
  ai: 'chrome-ai',
  compare: 'chrome-compare',
  liveRibbon: 'live-ribbon',
  liveTimeline: 'live-timeline',
  liveBriefing: 'live-briefing',
  liveRecovery: 'live-recovery',
  liveSignals: 'live-signals',
  perfHero: 'perf-hero',
  perfScorecard: 'perf-scorecard',
  perfOutlets: 'perf-outlets',
  settingsMatrix: 'settings-matrix',
  settingsIssues: 'settings-issues',
  agentPanel: 'agent-panel',
  agentComposer: 'agent-composer',
  perfDrill: 'perf-drill',
  perfDrillOpportunity: 'perf-drill-opportunity',
  liveDrill: 'live-drill',
  northStar: 'north-star',
  reviews: 'reviews-drill',
  comparison: 'comparison-view',
  integrations: 'integrations-drawer',
  scopeGroups: 'scope-groups-modal',
  outletDrawer: 'outlet-drawer',
  focusDrawer: 'focus-drawer',
  reservationDrawer: 'reservation-drawer',
  posConnect: 'pos-connect',
  marketingWizard: 'marketing-wizard',
  marketingAttribution: 'marketing-attribution',
  horizonHero: 'horizon-hero',
  horizonCourseMap: 'horizon-course-map',
  horizonDayDetail: 'horizon-day-detail',
  horizonCorrections: 'horizon-corrections',
  horizonInspector: 'horizon-inspector',
  assistantAsk: 'assistant-ask',
  assistantNotes: 'assistant-notes',
  assistantDashboard: 'assistant-dashboard',
} as const;

const FTUE_TARGET_BASE: Omit<FtueTarget, 'actions'>[] = [
  // —— Chrome ——
  {
    id: 'chrome.nav',
    surface: 'chrome',
    anchor: FTUE_ANCHORS.navModes,
    title: 'Move between views',
    body: "maître d'AI is your restaurant in conversation. Performance is the story over time. Forecast is the upcoming book. Growth is how guests find you. Live is tonight. Settings shows config that can cost covers.",
    diagram: 'nav',
    keywords: ['navigation', 'tabs', 'live', 'performance', 'forecast', 'horizon', 'settings', 'growth', 'maitre'],
  },
  {
    id: 'chrome.scope',
    surface: 'chrome',
    anchor: FTUE_ANCHORS.scope,
    title: 'Choose where you’re looking',
    body: 'Set one outlet, a saved group, or the full fleet. Every view follows this scope.',
    diagram: 'scope',
    keywords: ['scope', 'outlet', 'group', 'fleet', 'restaurant'],
  },
  {
    id: 'chrome.period',
    surface: 'chrome',
    anchor: FTUE_ANCHORS.period,
    title: 'Set the time window',
    body: 'Pick the period and how buckets roll up. Comparisons use the same control.',
    diagram: 'period',
    keywords: ['period', 'date', 'range', 'aggregation', 'capacity'],
  },
  {
    id: 'chrome.status',
    surface: 'chrome',
    anchor: FTUE_ANCHORS.status,
    title: 'Filter who counts',
    body: 'Include or drop statuses and reservation sources so numbers match the question you’re asking.',
    diagram: 'status',
    keywords: ['status', 'source', 'filter', 'include', 'walk-in'],
  },
  {
    id: 'chrome.ai',
    surface: 'chrome',
    anchor: FTUE_ANCHORS.ai,
    title: 'Ask in plain language',
    body: 'Open the AI lane for follow-ups, North Star setup, and dig-ins without leaving the board.',
    diagram: 'ai',
    keywords: ['ai', 'ask', 'agent', 'assistant', 'north star'],
  },
  {
    id: 'chrome.help',
    surface: 'chrome',
    anchor: FTUE_ANCHORS.navHelp,
    title: 'Replay this guide anytime',
    body: 'The question mark reopens help for the screen you’re on, or the full catalog.',
    diagram: 'generic',
    keywords: ['help', 'tour', 'guide', 'ftue'],
  },
  {
    id: 'chrome.sources',
    surface: 'chrome',
    anchor: FTUE_ANCHORS.navSources,
    title: 'Connect data sources',
    body: 'See what’s feeding the board — reservations, POS, reviews, marketing — and open integrations.',
    diagram: 'sources',
    keywords: ['integrations', 'data sources', 'pos', 'reviews'],
  },
  {
    id: 'chrome.compare',
    surface: 'chrome',
    anchor: FTUE_ANCHORS.compare,
    title: 'Compare scopes side by side',
    body: 'From the scope menu, pick 2–5 groups or outlets and read them together.',
    diagram: 'compare',
    keywords: ['compare', 'comparison', 'side by side'],
    contextualWhenMounted: true,
  },

  // —— Performance ——
  {
    id: 'perf.hero',
    surface: 'performance',
    anchor: FTUE_ANCHORS.perfHero,
    title: 'Start with what changed',
    body: 'The hero calls out the situations that matter in this period — flip a card for why and what to try.',
    diagram: 'performance-story',
    keywords: ['hero', 'situation', 'patterns', 'insight'],
  },
  {
    id: 'perf.scorecard',
    surface: 'performance',
    anchor: FTUE_ANCHORS.perfScorecard,
    title: 'Scan the period scorecard',
    body: 'Each tile is a KPI for the selected scope. Click one to open evidence and drivers.',
    diagram: 'scorecard',
    keywords: ['scorecard', 'kpi', 'metric', 'covers', 'noshow'],
  },
  {
    id: 'perf.outlets',
    surface: 'performance',
    anchor: FTUE_ANCHORS.perfOutlets,
    title: 'Find outlet exceptions',
    body: 'The table ranks outlets by momentum. Open a row for period detail on that site.',
    diagram: 'generic',
    keywords: ['outlet', 'table', 'ranking', 'momentum'],
  },
  {
    id: 'perf.northstar',
    surface: 'performance',
    anchor: FTUE_ANCHORS.northStar,
    title: 'Track your North Star',
    body: 'A composite goal you define — progress, components, and an action plan live here.',
    diagram: 'north-star',
    keywords: ['north star', 'goal', 'target'],
    contextualWhenMounted: true,
    catalogNote: 'Appears after you set a North Star with the AI.',
  },

  // —— Live ——
  {
    id: 'live.ribbon',
    surface: 'live',
    anchor: FTUE_ANCHORS.liveRibbon,
    title: 'Read tonight’s service',
    body: 'One restaurant at a time: pressure so far, right now, and what’s still booked.',
    diagram: 'timeline',
    keywords: ['live', 'service', 'ribbon', 'tonight'],
  },
  {
    id: 'live.timeline',
    surface: 'live',
    anchor: FTUE_ANCHORS.liveTimeline,
    title: 'Watch guest pressure',
    body: 'Switch between the load curve and the reservation board. The playhead is wall-clock now.',
    diagram: 'timeline',
    keywords: ['timeline', 'gantt', 'curve', 'bookings'],
  },
  {
    id: 'live.recovery',
    surface: 'live',
    anchor: FTUE_ANCHORS.liveRecovery,
    title: 'Act on recoverable gaps',
    body: 'Gaps you can still fill — focus an outlet and push recovery while the shift is open.',
    diagram: 'recovery',
    keywords: ['recovery', 'gaps', 'act', 'walk-in'],
  },
  {
    id: 'live.signals',
    surface: 'live',
    anchor: FTUE_ANCHORS.liveSignals,
    title: 'Check extra signals',
    body: 'Sell items, weather, and local events when those sources are connected.',
    diagram: 'generic',
    keywords: ['signals', 'weather', 'events', 'sell'],
  },
  {
    id: 'live.briefing',
    surface: 'live',
    anchor: FTUE_ANCHORS.liveBriefing,
    title: 'Prepare for tonight’s guests',
    body: 'The briefing brings party mix, arrival peaks, VIPs, requests, and overdue tables into one service-ready read.',
    diagram: 'timeline',
    keywords: ['briefing', 'arrivals', 'vip', 'requests', 'guest tags'],
  },

  // —— Settings ——
  {
    id: 'settings.matrix',
    surface: 'settings',
    anchor: FTUE_ANCHORS.settingsMatrix,
    title: 'Spot unhealthy settings',
    body: 'A matrix of shift config across outlets. Red blocks reservations; amber is worth a look.',
    diagram: 'settings-matrix',
    keywords: ['settings', 'health', 'matrix', 'capacity', 'shift'],
  },
  {
    id: 'settings.issues',
    surface: 'settings',
    anchor: FTUE_ANCHORS.settingsIssues,
    title: 'Prioritize fixes',
    body: 'Grouped issues with impact context so you fix the costly ones first.',
    diagram: 'settings-matrix',
    keywords: ['issues', 'findings', 'fixes'],
  },

  // —— AI ——
  {
    id: 'ai.panel',
    surface: 'ai',
    anchor: FTUE_ANCHORS.agentPanel,
    title: 'Your AI lane',
    body: 'Ask questions in context of the current scope and period. Switch to North Star setup when you need a goal.',
    diagram: 'ai',
    keywords: ['ai', 'chat', 'lane', 'drawer'],
  },
  {
    id: 'ai.composer',
    surface: 'ai',
    anchor: FTUE_ANCHORS.agentComposer,
    title: 'Type a follow-up',
    body: 'Use a chip or write your own question. Answers can navigate you to the right screen.',
    diagram: 'ai',
    keywords: ['composer', 'prompt', 'question'],
  },

  // —— maître d'AI (full-page Notes) ——
  {
    id: 'assistant.ask',
    surface: 'assistant',
    anchor: FTUE_ANCHORS.assistantAsk,
    title: 'Ask about this restaurant',
    body: 'Type a question or tap a starter. maître d’AI looks up this restaurant and explains the numbers in plain language.',
    diagram: 'assistant-ask',
    keywords: ['ask', 'question', 'covers', 'notes', 'maitre', 'assistant'],
  },
  {
    id: 'assistant.notes',
    surface: 'assistant',
    anchor: FTUE_ANCHORS.assistantNotes,
    title: 'Notes keep the answers',
    body: 'Substantive answers become written notes you can scroll back through — a briefing, not a chat thread.',
    diagram: 'assistant-notes',
    keywords: ['notes', 'chapters', 'answers', 'notebook'],
  },
  {
    id: 'assistant.dashboard',
    surface: 'assistant',
    anchor: FTUE_ANCHORS.assistantDashboard,
    title: 'Keep what you watch',
    body: 'Pin a chart or a figure from any note so it stays on your dashboard.',
    diagram: 'assistant-pin',
    keywords: ['dashboard', 'pin', 'watch', 'tiles'],
  },

  // —— Drills & secondary ——
  {
    id: 'drill.perf',
    surface: 'perf-drill',
    anchor: FTUE_ANCHORS.perfDrill,
    title: 'Open the evidence deck',
    body: 'Trend, sources, drivers, and outlet ranking for one metric — all under the same filters.',
    diagram: 'drill',
    keywords: ['drill', 'evidence', 'drivers', 'trend'],
  },
  {
    id: 'drill.opportunity',
    surface: 'perf-drill',
    anchor: FTUE_ANCHORS.perfDrillOpportunity,
    title: 'Read the opportunity',
    body: 'A short narrative of what’s moving the metric and what to try next.',
    diagram: 'drill',
    keywords: ['opportunity', 'narrative', 'action'],
  },
  {
    id: 'drill.live',
    surface: 'live-drill',
    anchor: FTUE_ANCHORS.liveDrill,
    title: 'Live metric drill-down',
    body: 'Deeper breakdown for a Live KPI without leaving the service board.',
    diagram: 'drill',
    keywords: ['live drill', 'breakdown'],
    catalogOnly: true,
    catalogNote: 'Opens when you click a Live KPI that supports drill-down.',
  },
  {
    id: 'drill.northstar',
    surface: 'north-star',
    anchor: FTUE_ANCHORS.northStar,
    title: 'North Star detail',
    body: 'Baseline, component metrics, drivers, and the generated action plan.',
    diagram: 'north-star',
    keywords: ['north star drill', 'plan'],
    catalogOnly: true,
    catalogNote: 'Open from the North Star hero on Performance.',
  },
  {
    id: 'drill.reviews',
    surface: 'reviews',
    anchor: FTUE_ANCHORS.reviews,
    title: 'Guest reviews',
    body: 'Sentiment and themes for the selected period when reviews are connected.',
    diagram: 'generic',
    keywords: ['reviews', 'sentiment', 'guest'],
    catalogOnly: true,
    catalogNote: 'Requires the reviews data source.',
  },
  {
    id: 'comparison.live',
    surface: 'comparison-live',
    anchor: FTUE_ANCHORS.comparison,
    title: 'Compare live service',
    body: 'The Live comparison aligns current service pressure across the selected scopes under the same filters.',
    diagram: 'compare',
    keywords: ['comparison', 'live', 'service', 'pressure', 'scopes'],
  },
  {
    id: 'comparison.perf',
    surface: 'comparison-perf',
    anchor: FTUE_ANCHORS.comparison,
    title: 'Compare performance',
    body: 'The Performance comparison aligns scorecards and trends across selected scopes for the same period.',
    diagram: 'compare',
    keywords: ['comparison', 'performance', 'scorecard', 'trend', 'period'],
  },
  {
    id: 'comparison.settings',
    surface: 'comparison-settings',
    anchor: FTUE_ANCHORS.comparison,
    title: 'Compare settings health',
    body: 'The Settings comparison shows where configuration risks repeat or differ across selected scopes.',
    diagram: 'settings-matrix',
    keywords: ['comparison', 'settings', 'health', 'configuration', 'risk'],
  },
  {
    id: 'sec.integrations',
    surface: 'integrations',
    anchor: FTUE_ANCHORS.integrations,
    title: 'Integrations drawer',
    body: 'Connect or mute sources that feed Live, Performance, and AI.',
    diagram: 'sources',
    keywords: ['integrations', 'connect', 'disconnect'],
    catalogOnly: true,
    catalogNote: 'Open from Data sources in the nav rail.',
  },
  {
    id: 'sec.scopeGroups',
    surface: 'scope-groups',
    anchor: FTUE_ANCHORS.scopeGroups,
    title: 'Manage groups',
    body: 'Create saved outlet groups for faster scoping and compare sets.',
    diagram: 'scope',
    keywords: ['groups', 'scope groups', 'manage'],
    catalogOnly: true,
    catalogNote: 'Open Groups from the scope menu.',
  },
  {
    id: 'sec.outletDrawer',
    surface: 'outlet-drawer',
    anchor: FTUE_ANCHORS.outletDrawer,
    title: 'Outlet period drawer',
    body: 'One outlet’s drill-down, opened from the Performance table or from a drill-down.',
    diagram: 'generic',
    keywords: ['outlet drawer', 'detail'],
    catalogOnly: true,
    catalogNote: 'Open an outlet row on Performance.',
  },
  {
    id: 'sec.focusDrawer',
    surface: 'focus-drawer',
    anchor: FTUE_ANCHORS.focusDrawer,
    title: 'Outlet focus drawer',
    body: 'Live snapshot for one outlet — useful when acting on recovery.',
    diagram: 'generic',
    keywords: ['focus', 'drawer'],
    catalogOnly: true,
    catalogNote: 'Open from Live recovery or an outlet card.',
  },
  {
    id: 'sec.reservationDrawer',
    surface: 'reservation-drawer',
    anchor: FTUE_ANCHORS.reservationDrawer,
    title: 'Inspect a reservation',
    body: 'The reservation drawer collects timing, guest tags, notes, floor assignment, and source without exposing contact details.',
    diagram: 'timeline',
    keywords: ['reservation', 'drawer', 'guest tags', 'notes', 'timing'],
  },
  {
    id: 'sec.posConnect',
    surface: 'pos-connect',
    anchor: FTUE_ANCHORS.posConnect,
    title: 'POS connect',
    body: 'Link baskets so average check and RevPASH use real till data.',
    diagram: 'sources',
    keywords: ['pos', 'connect', 'basket'],
    catalogOnly: true,
    catalogNote: 'Opened from Integrations when POS is available.',
  },
  {
    id: 'sec.marketingWizard',
    surface: 'marketing-wizard',
    anchor: FTUE_ANCHORS.marketingWizard,
    title: 'Marketing connect',
    body: 'Map Google Ads / Analytics / Search Console properties to outlets.',
    diagram: 'sources',
    keywords: ['marketing', 'google ads', 'ga', 'gsc'],
    catalogOnly: true,
    catalogNote: 'Opened from Integrations when marketing sources are available.',
  },
  {
    id: 'sec.marketingAttribution',
    surface: 'marketing-attribution',
    anchor: FTUE_ANCHORS.marketingAttribution,
    title: 'Marketing attribution',
    body: 'See what one channel brought in: visits, and the reservations traced to it by a tag.',
    diagram: 'generic',
    keywords: ['attribution', 'marketing drill', 'channel'],
    catalogOnly: true,
    catalogNote: 'Open from the Sources X-ray in Growth.',
  },
  {
    id: 'horizon.hero',
    surface: 'horizon',
    anchor: FTUE_ANCHORS.horizonHero,
    title: 'Start with how the stretch is shaping up',
    body: 'One sentence for the chosen stretch — next 7, 14 or 30 days, this month or next — then the numbers behind it: guests booked, how full, against last year and against recent weeks at the same point, the last week of reservations, and an expected final once the forecast has proven itself on past dates.',
    diagram: 'scorecard',
    keywords: [
      'horizon',
      'outlook',
      'on the books',
      'last year',
      'recent weeks',
      'ahead',
      'behind',
      'forecast',
      'occupancy',
    ],
  },
  {
    id: 'horizon.courseMap',
    surface: 'horizon',
    anchor: FTUE_ANCHORS.horizonCourseMap,
    title: 'See the shape of the reservation window',
    body: 'A bar per upcoming date for guests booked now, with lines for what last year and recent weeks had booked at the same point, seats available, and the forecast range where it is accurate enough to show. Click a date to open it at the bottom of the page.',
    diagram: 'generic',
    keywords: [
      'horizon',
      'chart',
      'shape',
      'pace',
      'last year',
      'upcoming',
      'covers',
    ],
  },
  {
    id: 'horizon.dayDetail',
    surface: 'horizon',
    anchor: FTUE_ANCHORS.horizonDayDetail,
    title: 'Run one upcoming day',
    body: 'At the bottom of the page, the chosen day’s run sheet: every reservation on a clock, the party mix and arrival peaks, the large parties, VIPs and notes that need a plan, and the channels and campaigns that brought the reservations in. Needs one outlet in focus, because guest tags come from one outlet at a time.',
    diagram: 'generic',
    keywords: [
      'day',
      'gantt',
      'timeline',
      'bookings',
      'large parties',
      'big groups',
      'vip',
      'campaigns',
      'channels',
      'arrivals',
    ],
  },
  {
    id: 'horizon.corrections',
    surface: 'horizon',
    anchor: FTUE_ANCHORS.horizonCorrections,
    title: 'Act on what stands out',
    body: 'Findings split by who acts — operations or marketing — biggest impact first, with the dates each one covers rather than the same sentence per date. A stretch or weekday running behind last year shows once, not once per date. Quiet when nothing stands out.',
    diagram: 'generic',
    keywords: ['insights', 'signals', 'behind', 'bookability', 'capacity', 'marketing', 'operations'],
  },
  {
    id: 'horizon.inspector',
    surface: 'horizon',
    anchor: FTUE_ANCHORS.horizonInspector,
    title: 'Inspect one date',
    body: 'Covers booked against last year and recent weeks at the same point, seats available, the expected final and its likely range when the forecast is accurate enough, and how many more guests a day a goal would need. Share a link to hand the date on.',
    diagram: 'drill',
    keywords: ['inspector', 'pace', 'forecast', 'target', 'share'],
  },
];

/**
 * Concrete operator moves for each help target. Keep these imperative,
 * verifiable in the product, and short enough to scan inside a spotlight.
 */
const FTUE_ACTIONS: Record<string, string[]> = {
  'chrome.nav': [
    "Switch to maître d'AI to ask, Performance for trends, Forecast for the upcoming book, or Settings for configuration health.",
    'Move between jobs without rebuilding the scope you are investigating.',
  ],
  'chrome.scope': [
    'Narrow every view to one outlet, a saved group, or the full fleet.',
    'Open Compare to place 2–5 outlets or groups side by side.',
    'Create and maintain reusable groups for regions or operating cohorts.',
  ],
  'chrome.period': [
    'Choose a preset or custom date window for the question you are answering.',
    'Change the roll-up to read the same period by day, week, or month.',
    'Use the comparison period to separate a one-off shift from a persistent change.',
  ],
  'chrome.status': [
    'Include or exclude reservation statuses before trusting a KPI or trend.',
    'Filter reservation sources to isolate channels such as direct, partner, or walk-in demand.',
    'Clear the filters to return every card and drill-down to the shared baseline.',
  ],
  'chrome.ai': [
    'Ask a follow-up about the current scope, period, metric, or outlet.',
    'Use an answer to navigate directly to supporting evidence in Ops Command.',
    'Open North Star setup when you need to define or revise a fleet goal.',
  ],
  'chrome.help': [
    'Run a short tour for the screen you are currently using.',
    'Replay the full operating-story tour or search help for a specific task.',
  ],
  'chrome.sources': [
    'Check which reservation, POS, review, and marketing feeds are available.',
    'Connect, configure, or mute a source when its data should change the board.',
    'See which locked metrics will become available after a connection.',
  ],
  'chrome.compare': [
    'Select 2–5 outlets or groups and inspect the same KPIs under identical filters.',
    'Change the compared scopes without leaving the Live, Performance, or Settings view.',
  ],
  'perf.hero': [
    'Open a situation to see why it matters, what may explain it, and what to try.',
    'Jump from the situation into the metric evidence that supports the narrative.',
    'Ask AI a follow-up while keeping the same scope and period.',
  ],
  'perf.scorecard': [
    'Scan deltas to find which KPI moved enough to deserve investigation.',
    'Select a KPI tile to open its trend, sources, drivers, and outlet ranking.',
    'Re-run the scorecard with a different period, status mix, or reservation source.',
  ],
  'perf.outlets': [
    'Sort a metric column to find the strongest and weakest outlet exceptions.',
    'Open an outlet row to inspect its period metrics and trend in context.',
    'Use the top-and-bottom view to focus attention before expanding the full table.',
  ],
  'perf.northstar': [
    'Open the detail view to check progress, component metrics, and drivers.',
    'Use AI to define or revise the goal and its weighting.',
    'Turn the generated action plan into the next operating review.',
  ],
  'live.ribbon': [
    'Choose an outlet and read pressure now, what has already happened, and what is still booked.',
    'Select a supported KPI to open its live breakdown without leaving service.',
    'Switch the scope when another outlet needs immediate attention.',
  ],
  'live.timeline': [
    'Switch between the load curve and reservation board to read pressure from two angles.',
    'Use the now marker to separate guests already in-house from arrivals still ahead.',
    'Scroll the reservation board to inspect the sequence and duration of upcoming demand.',
  ],
  'live.recovery': [
    'Focus the outlet with a recoverable gap while there is still time to act.',
    'Open its live snapshot before choosing the recovery action.',
    'Use the gap size and timing to prioritize where intervention can still matter.',
  ],
  'live.signals': [
    'Check whether sell items, weather, or local events help explain unusual demand.',
    'Open Data sources when a missing signal should be connected.',
    'Treat these as context for a decision, not a replacement for reservation evidence.',
  ],
  'live.briefing': [
    'Scan party sizes and arrival peaks before the service rush.',
    'Open VIP, request, or overdue reservations that need staff attention.',
    'Use guest tags and floor notes to brief the kitchen and host team.',
  ],
  'settings.matrix': [
    'Scan across outlets to find repeated capacity or shift-configuration problems.',
    'Separate reservation-blocking red cells from amber items that need review.',
    'Change scope to verify whether a problem is local or fleet-wide.',
  ],
  'settings.issues': [
    'Start with the issue carrying the clearest reservation or capacity impact.',
    'Use the grouped context to decide which outlet and setting to correct first.',
    'Return after changes to confirm the issue no longer appears in the audit.',
  ],
  'ai.panel': [
    'Ask about the active scope and period without restating that context.',
    'Follow evidence links or navigation actions returned with an answer.',
    'Switch to North Star mode when the task is goal definition rather than diagnosis.',
  ],
  'ai.composer': [
    'Choose a suggested prompt for a fast, grounded starting point.',
    'Write a specific question naming the metric, outlet, or decision you need.',
    'Refine the answer with follow-ups instead of opening a separate analysis.',
  ],
  'assistant.ask': [
    'Type a question about covers, no-shows, channels, or tonight.',
    'Tap a starter question to see how an answer lands in Notes.',
  ],
  'assistant.notes': [
    'Scroll back through written answers for this restaurant.',
    'Start from scratch when you want a clean page of notes.',
  ],
  'assistant.dashboard': [
    'Keep a chart or number on the dashboard to watch it daily.',
    'Open a pinned answer to read the full note again.',
  ],
  'drill.perf': [
    'Check the trend first, then use sources and drivers to test the likely explanation.',
    'Compare outlet ranking to learn whether the movement is broad or concentrated.',
    'Move to another metric while preserving the active scope and filters.',
  ],
  'drill.opportunity': [
    'Use the narrative to identify the operational lever connected to the metric movement.',
    'Open Details to inspect the evidence before acting on the recommendation.',
    'Carry the suggested next move into the outlet or period that needs attention.',
  ],
  'drill.live': [
    'Inspect the live KPI breakdown to locate where current pressure is coming from.',
    'Return to the service board after identifying the outlet or time window to watch.',
  ],
  'drill.northstar': [
    'Compare current progress with the baseline and target.',
    'Inspect component metrics and drivers to find what is holding the goal back.',
    'Use the action plan to choose the next measurable intervention.',
  ],
  'drill.reviews': [
    'Compare sentiment and recurring themes over the selected period.',
    'Identify outlets or topics that need an operating response.',
    'Change the period to test whether a theme is current or persistent.',
  ],
  'comparison.live': [
    'Scan each scope for the strongest current service pressure.',
    'Compare arrivals, occupancy, and recovery gaps under the same filters.',
    'Edit the compare set when another outlet or group needs attention.',
  ],
  'comparison.perf': [
    'Compare the same KPI across scopes under one period and filter set.',
    'Use aligned trends to separate broad movement from local exceptions.',
    'Edit the compare set when a different operating peer is more useful.',
  ],
  'comparison.settings': [
    'Compare configuration health across the selected scopes.',
    'Find risks that repeat across outlets and exceptions that stay local.',
    'Edit the compare set before prioritizing the next settings fix.',
  ],
  'sec.integrations': [
    'Connect a source to unlock the metrics and context it supplies.',
    'Manage outlet mappings for sources that need configuration.',
    'Mute a feed when it should temporarily stop shaping Ops Command.',
  ],
  'sec.scopeGroups': [
    'Create a reusable group for a region, format, or operating cohort.',
    'Edit group membership as the fleet changes.',
    'Use saved groups in Scope and Compare instead of selecting outlets repeatedly.',
  ],
  'sec.outletDrawer': [
    'Review one outlet’s trend, source mix and drivers without losing the fleet table.',
    'Switch metrics at the top of the drawer; it stays on the same outlet.',
    'Use the outlet detail to decide whether a fleet signal needs a local response.',
  ],
  'sec.focusDrawer': [
    'Check the outlet’s live status, occupancy, arrivals, and connected signals together.',
    'Use available outlet actions after confirming the recovery context.',
    'Close the drawer to return to the exact place on the Live board.',
  ],
  'sec.reservationDrawer': [
    'Check the reservation state, service timing, and assigned tables.',
    'Read guest tags and notes before the party arrives or while it is seated.',
    'Close the drawer to return to the same place on the service board.',
  ],
  'sec.posConnect': [
    'Review which revenue and basket metrics the POS connection will unlock.',
    'Start the connection flow when the outlet mapping and source are ready.',
  ],
  'sec.marketingWizard': [
    'Select the external account or property that belongs to each outlet.',
    'Review and save mappings so campaign activity can join reservation outcomes.',
    'Return later to correct mappings as accounts or outlets change.',
  ],
  'sec.marketingAttribution': [
    'Read one channel’s visits and reservations for the selected period.',
    'Check how many of its reservations are tagged; untagged ones stay in Not matched.',
    'See the sources, pages, or campaigns feeding it, and where the measurement stops.',
  ],
  'horizon.hero': [
    'Read whether the coming stretch is ahead of or behind last year and recent weeks.',
    'Switch between the next 7, 14 or 30 days, this month and next month.',
    'See the expected final only where it beat a simple same-weekday average on past dates.',
  ],
  'horizon.courseMap': [
    'Spot the dates running below last year at the same point.',
    'Compare the book with seats available, date by date.',
    'Click a date to open its detail at the bottom of the page.',
  ],
  'horizon.dayDetail': [
    'Read the whole service on one clock and spot the arrival crush before it happens.',
    'Plan the large parties, VIPs, and reservations carrying a note.',
    'See which channels and campaigns filled the day, and how many reservations carry no attribution at all.',
  ],
  'horizon.corrections': [
    'Start with the highest-ranked finding — guests at stake across every date it affects.',
    'Click a date on the card to open it before promoting or locking a service.',
    'Acknowledge or hide a card on this browser; share a URL to brief a colleague.',
  ],
  'horizon.inspector': [
    'Read covers booked separately from the forecast of who will actually arrive.',
    'Compare the date with last year and recent weeks at the same days ahead.',
    'Enter a goal to see how many more guests a day it needs — no campaign lift assumed.',
  ],
};

export const FTUE_TARGETS: FtueTarget[] = FTUE_TARGET_BASE.map(target =>
  withLocalizedCopy({
    ...target,
    actions: FTUE_ACTIONS[target.id] ?? [],
  }),
);

const clearAndMode = (mode: FtueMode): FtueTourStep['prepare'] => [
  { kind: 'closeOverlays' },
  { kind: 'clearComparison' },
  { kind: 'mode', mode },
];

const FTUE_JOURNEY_BASE: FtueJourney[] = [
  {
    id: 'full',
    kind: 'full',
    label: 'Full product tour',
    steps: [
      {
        id: 'tour.nav',
        targetId: 'chrome.nav',
        prepare: clearAndMode('performance'),
      },
      {
        id: 'tour.scope',
        targetId: 'chrome.scope',
        prepare: [{ kind: 'mode', mode: 'performance' }],
      },
      {
        id: 'tour.periodStatus',
        targetId: 'chrome.period',
        prepare: [{ kind: 'mode', mode: 'performance' }],
      },
      {
        id: 'tour.performance',
        targetId: 'perf.hero',
        prepare: [
          { kind: 'closePerfDrill' },
          { kind: 'mode', mode: 'performance' },
        ],
        deepDiveJourneyId: 'deep.perf.zones',
      },
      {
        id: 'tour.drill',
        targetId: 'drill.perf',
        waitMs: 12000,
        required: true,
        prepare: [
          { kind: 'mode', mode: 'performance' },
          { kind: 'openPerfDrill', metricKey: 'covers' },
        ],
        deepDiveJourneyId: 'deep.perf.drill',
      },
      {
        id: 'tour.ai',
        targetId: 'ai.panel',
        waitMs: 4000,
        required: true,
        prepare: [
          { kind: 'closePerfDrill' },
          { kind: 'mode', mode: 'performance' },
          { kind: 'openAgent' },
        ],
      },
    ],
  },
  {
    id: 'surface.live',
    kind: 'surface',
    label: 'Tour this screen',
    surface: 'live',
    steps: [
      {
        id: 'live.nav',
        targetId: 'chrome.nav',
        prepare: clearAndMode('live'),
      },
      {
        id: 'live.scope',
        targetId: 'chrome.scope',
        prepare: [{ kind: 'mode', mode: 'live' }],
      },
      {
        id: 'live.board',
        targetId: 'live.ribbon',
        prepare: [{ kind: 'mode', mode: 'live' }],
        deepDiveJourneyId: 'deep.live.zones',
      },
      {
        id: 'live.act',
        targetId: 'live.recovery',
        prepare: [{ kind: 'mode', mode: 'live' }],
      },
    ],
  },
  {
    id: 'surface.performance',
    kind: 'surface',
    label: 'Tour this screen',
    surface: 'performance',
    steps: [
      {
        id: 'perf.scope',
        targetId: 'chrome.scope',
        prepare: clearAndMode('performance'),
      },
      {
        id: 'perf.period',
        targetId: 'chrome.period',
        prepare: [{ kind: 'mode', mode: 'performance' }],
      },
      {
        id: 'perf.story',
        targetId: 'perf.hero',
        prepare: [
          { kind: 'closePerfDrill' },
          { kind: 'mode', mode: 'performance' },
        ],
        deepDiveJourneyId: 'deep.perf.zones',
      },
      {
        id: 'perf.ask',
        targetId: 'chrome.ai',
        prepare: [{ kind: 'mode', mode: 'performance' }],
      },
    ],
  },
  {
    id: 'surface.settings',
    kind: 'surface',
    label: 'Tour this screen',
    surface: 'settings',
    steps: [
      {
        id: 'settings.nav',
        targetId: 'chrome.nav',
        prepare: clearAndMode('settings'),
      },
      {
        id: 'settings.scope',
        targetId: 'chrome.scope',
        prepare: [{ kind: 'mode', mode: 'settings' }],
      },
      {
        id: 'settings.health',
        targetId: 'settings.matrix',
        prepare: [{ kind: 'mode', mode: 'settings' }],
        deepDiveJourneyId: 'deep.settings.zones',
      },
      {
        id: 'settings.sources',
        targetId: 'chrome.sources',
        prepare: [{ kind: 'mode', mode: 'settings' }],
      },
    ],
  },
  {
    id: 'surface.horizon',
    kind: 'surface',
    label: 'Tour this screen',
    surface: 'horizon',
    steps: [
      {
        id: 'horizon.nav',
        targetId: 'chrome.nav',
        prepare: clearAndMode('horizon'),
      },
      {
        id: 'horizon.scope',
        targetId: 'chrome.scope',
        prepare: [{ kind: 'mode', mode: 'horizon' }],
      },
      {
        id: 'horizon.highlights',
        targetId: 'horizon.hero',
        prepare: [{ kind: 'mode', mode: 'horizon' }],
      },
      {
        id: 'horizon.map',
        targetId: 'horizon.courseMap',
        prepare: [{ kind: 'mode', mode: 'horizon' }],
      },
      {
        id: 'horizon.act',
        targetId: 'horizon.corrections',
        prepare: [{ kind: 'mode', mode: 'horizon' }],
      },
      {
        id: 'horizon.inspect',
        targetId: 'horizon.inspector',
        prepare: [{ kind: 'mode', mode: 'horizon' }],
      },
      {
        id: 'horizon.day',
        targetId: 'horizon.dayDetail',
        prepare: [{ kind: 'mode', mode: 'horizon' }],
      },
    ],
  },
  {
    id: 'surface.comparison.live',
    kind: 'surface',
    label: 'Tour this comparison',
    surface: 'comparison-live',
    steps: [
      {
        id: 'comparison.live.controls',
        targetId: 'chrome.compare',
        prepare: [{ kind: 'mode', mode: 'live' }],
      },
      {
        id: 'comparison.live.board',
        targetId: 'comparison.live',
        prepare: [{ kind: 'mode', mode: 'live' }],
      },
    ],
  },
  {
    id: 'surface.comparison.performance',
    kind: 'surface',
    label: 'Tour this comparison',
    surface: 'comparison-perf',
    steps: [
      {
        id: 'comparison.performance.controls',
        targetId: 'chrome.compare',
        prepare: [{ kind: 'mode', mode: 'performance' }],
      },
      {
        id: 'comparison.performance.board',
        targetId: 'comparison.perf',
        prepare: [{ kind: 'mode', mode: 'performance' }],
      },
    ],
  },
  {
    id: 'surface.comparison.settings',
    kind: 'surface',
    label: 'Tour this comparison',
    surface: 'comparison-settings',
    steps: [
      {
        id: 'comparison.settings.controls',
        targetId: 'chrome.compare',
        prepare: [{ kind: 'mode', mode: 'settings' }],
      },
      {
        id: 'comparison.settings.board',
        targetId: 'comparison.settings',
        prepare: [{ kind: 'mode', mode: 'settings' }],
      },
    ],
  },
  {
    id: 'deep.live.zones',
    kind: 'deep',
    label: 'Live board details',
    parentJourneyId: 'surface.live',
    steps: [
      {
        id: 'deep.live.timeline',
        targetId: 'live.timeline',
        prepare: [{ kind: 'mode', mode: 'live' }],
      },
      {
        id: 'deep.live.recovery',
        targetId: 'live.recovery',
        prepare: [{ kind: 'mode', mode: 'live' }],
      },
      {
        id: 'deep.live.briefing',
        targetId: 'live.briefing',
        prepare: [{ kind: 'mode', mode: 'live' }],
      },
      {
        id: 'deep.live.signals',
        targetId: 'live.signals',
        prepare: [{ kind: 'mode', mode: 'live' }],
      },
    ],
  },
  {
    id: 'deep.perf.zones',
    kind: 'deep',
    label: 'Performance details',
    parentJourneyId: 'surface.performance',
    steps: [
      {
        id: 'deep.perf.scorecard',
        targetId: 'perf.scorecard',
        prepare: [
          { kind: 'closePerfDrill' },
          { kind: 'mode', mode: 'performance' },
        ],
      },
      {
        id: 'deep.perf.outlets',
        targetId: 'perf.outlets',
        prepare: [{ kind: 'mode', mode: 'performance' }],
      },
      {
        id: 'deep.perf.status',
        targetId: 'chrome.status',
        prepare: [{ kind: 'mode', mode: 'performance' }],
      },
    ],
  },
  {
    id: 'deep.perf.drill',
    kind: 'deep',
    label: 'Evidence details',
    parentJourneyId: 'full',
    steps: [
      {
        id: 'deep.drill.opportunity',
        targetId: 'drill.opportunity',
        waitMs: 12000,
        prepare: [
          { kind: 'mode', mode: 'performance' },
          { kind: 'openPerfDrill', metricKey: 'covers' },
        ],
      },
    ],
  },
  {
    id: 'deep.settings.zones',
    kind: 'deep',
    label: 'Settings details',
    parentJourneyId: 'surface.settings',
    steps: [
      {
        id: 'deep.settings.issues',
        targetId: 'settings.issues',
        prepare: [{ kind: 'mode', mode: 'settings' }],
      },
    ],
  },
];

export const FTUE_JOURNEYS: FtueJourney[] = FTUE_JOURNEY_BASE.map(journey => ({
  ...journey,
  get label() {
    return resolveFtueJourneyLabel(journey.id);
  },
}));

/** @deprecated Prefer FTUE_JOURNEYS; kept as the full-tour step list. */
export const FTUE_TOUR_STEPS: FtueTourStep[] =
  FTUE_JOURNEYS.find(j => j.id === 'full')?.steps ?? [];

const FTUE_SURFACE_IDS: FtueSurfaceId[] = [
  'chrome',
  'live',
  'performance',
  'settings',
  'ai',
  'perf-drill',
  'live-drill',
  'north-star',
  'reviews',
  'comparison-live',
  'comparison-perf',
  'comparison-settings',
  'integrations',
  'scope-groups',
  'outlet-drawer',
  'focus-drawer',
  'reservation-drawer',
  'pos-connect',
  'marketing-wizard',
  'marketing-attribution',
  'horizon',
  'assistant',
];

export const FTUE_SURFACE_LABELS = {} as Record<FtueSurfaceId, string>;
for (const surface of FTUE_SURFACE_IDS) {
  Object.defineProperty(FTUE_SURFACE_LABELS, surface, {
    enumerable: true,
    configurable: true,
    get: () => resolveFtueSurfaceLabel(surface),
  });
}

const TARGET_BY_ID = new Map(FTUE_TARGETS.map(t => [t.id, t]));
const JOURNEY_BY_ID = new Map(FTUE_JOURNEYS.map(j => [j.id, j]));

export function getFtueTarget(id: string): FtueTarget | undefined {
  return TARGET_BY_ID.get(id);
}

export function getFtueJourney(id: FtueJourneyId | null | undefined): FtueJourney | undefined {
  if (!id) return undefined;
  return JOURNEY_BY_ID.get(id);
}

export function getTourStepIndex(
  stepId: string | null | undefined,
  journeyId: FtueJourneyId = 'full',
): number {
  if (!stepId) return 0;
  const journey = getFtueJourney(journeyId);
  if (!journey) return 0;
  const i = journey.steps.findIndex(s => s.id === stepId);
  return i >= 0 ? i : 0;
}

export function surfaceJourneyIdForMode(mode: FtueMode): FtueJourneyId {
  if (mode === 'live') return 'surface.live';
  if (mode === 'settings') return 'surface.settings';
  if (mode === 'horizon') return 'surface.horizon';
  return 'surface.performance';
}

export function surfaceJourneyIdForSurface(
  surface: FtueSurfaceId,
  fallbackMode: FtueMode,
): FtueJourneyId {
  if (surface === 'comparison-live') return 'surface.comparison.live';
  if (surface === 'comparison-perf') return 'surface.comparison.performance';
  if (surface === 'comparison-settings') return 'surface.comparison.settings';
  return surfaceJourneyIdForMode(fallbackMode);
}

/** Resolve which surface the user is currently looking at. */
export function resolveActiveSurface(
  ctx: FtueSurfaceContext,
): FtueSurfaceId {
  if (ctx.posConnectOpen) return 'pos-connect';
  if (ctx.marketingWizardOpen) return 'marketing-wizard';
  if (ctx.marketingDrillOpen) return 'marketing-attribution';
  if (ctx.integrationsOpen) return 'integrations';
  if (ctx.scopeSettingsOpen) return 'scope-groups';
  if (ctx.reservationDrawerOpen) return 'reservation-drawer';
  if (ctx.focusDrawerOpen) return 'focus-drawer';
  if (ctx.outletDrawerOpen) return 'outlet-drawer';
  if (ctx.reviewsDrillOpen) return 'reviews';
  if (ctx.northStarDrillOpen) return 'north-star';
  if (ctx.liveDrillOpen) return 'live-drill';
  if (ctx.perfDrillOpen) return 'perf-drill';
  if (ctx.assistantOpen) return 'assistant';
  if (ctx.agentOpen) return 'ai';
  if (ctx.isComparing) {
    if (ctx.mode === 'live') return 'comparison-live';
    if (ctx.mode === 'settings') return 'comparison-settings';
    return 'comparison-perf';
  }
  return ctx.mode;
}

const SETTINGS_HIDDEN_CHROME = new Set(['chrome.period', 'chrome.status']);

function targetIsContextual(t: FtueTarget): boolean {
  if (t.catalogOnly) return false;
  if (!t.contextualWhenMounted) return true;
  if (typeof document === 'undefined' || !t.anchor) return false;
  try {
    return !!document.querySelector(
      `[data-ftue="${CSS.escape(t.anchor)}"]`,
    );
  } catch {
    return !!document.querySelector(`[data-ftue="${t.anchor}"]`);
  }
}

function chromeTargetsForSurface(surface: FtueSurfaceId): FtueTarget[] {
  return FTUE_TARGETS.filter(t => {
    if (
      t.surface !== 'chrome' ||
      !targetIsContextual(t) ||
      t.id === 'chrome.help'
    ) {
      return false;
    }
    if (
      (surface === 'settings' || surface === 'horizon' || surface === 'assistant') &&
      SETTINGS_HIDDEN_CHROME.has(t.id)
    ) {
      return false;
    }
    return true;
  });
}

/** Targets that belong on the current surface map (visible anchors preferred). */
export function targetsForSurface(surface: FtueSurfaceId): FtueTarget[] {
  const primary = FTUE_TARGETS.filter(
    t => t.surface === surface && targetIsContextual(t),
  );
  if (
    surface === 'live' ||
    surface === 'performance' ||
    surface === 'settings' ||
    surface === 'horizon' ||
    surface.startsWith('comparison-')
  ) {
    return [...chromeTargetsForSurface(surface), ...primary];
  }
  if (surface === 'ai') {
    return FTUE_TARGETS.filter(t => t.surface === 'ai' || t.id === 'chrome.ai');
  }
  return primary.length
    ? primary
    : FTUE_TARGETS.filter(t => t.surface === surface);
}

/** Content targets first; shared chrome listed separately for help grouping. */
export function groupedTargetsForSurface(surface: FtueSurfaceId): {
  content: FtueTarget[];
  chrome: FtueTarget[];
} {
  if (
    surface === 'live' ||
    surface === 'performance' ||
    surface === 'settings' ||
    surface === 'horizon' ||
    surface.startsWith('comparison-')
  ) {
    return {
      content: FTUE_TARGETS.filter(
        t => t.surface === surface && targetIsContextual(t),
      ),
      chrome: chromeTargetsForSurface(surface),
    };
  }
  const all = targetsForSurface(surface);
  return { content: all, chrome: [] };
}

export function validateFtueRegistry(): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  const anchors = new Map<string, string>();
  const knownActions = new Set([
    'mode',
    'openPerfDrill',
    'closePerfDrill',
    'openAgent',
    'closeAgent',
    'closeOverlays',
    'clearComparison',
  ]);
  for (const t of FTUE_TARGETS) {
    if (ids.has(t.id)) errors.push(`Duplicate target id: ${t.id}`);
    ids.add(t.id);
    if (!t.title.trim()) errors.push(`Empty title: ${t.id}`);
    if (!t.body.trim()) errors.push(`Empty body: ${t.id}`);
    if (t.actions.length < 2 || t.actions.length > 3) {
      errors.push(
        `Target ${t.id} should have 2–3 operator actions, got ${t.actions.length}`,
      );
    }
    if (t.actions.some(action => !action.trim())) {
      errors.push(`Target ${t.id} has an empty operator action`);
    }
    if (!t.keywords.length || t.keywords.some(keyword => !keyword.trim())) {
      errors.push(`Target ${t.id} should have searchable keywords`);
    }
    if (!t.catalogOnly && !t.anchor) {
      errors.push(`Missing anchor for non-catalog target: ${t.id}`);
    }
    if (t.anchor) {
      const prev = anchors.get(t.anchor);
      if (prev && prev === t.id) {
        errors.push(`Anchor mapped twice to same id: ${t.anchor}`);
      }
      anchors.set(t.anchor, t.id);
    }
  }

  for (const surface of Object.keys(FTUE_SURFACE_LABELS) as FtueSurfaceId[]) {
    if (
      surface !== 'chrome' &&
      !FTUE_TARGETS.some(target => target.surface === surface)
    ) {
      errors.push(`Navigable surface ${surface} has no target`);
    }
  }

  const journeyIds = new Set<string>();
  for (const journey of FTUE_JOURNEYS) {
    if (journeyIds.has(journey.id)) {
      errors.push(`Duplicate journey id: ${journey.id}`);
    }
    journeyIds.add(journey.id);
    if (!journey.steps.length) {
      errors.push(`Journey ${journey.id} has no steps`);
    }
    if (journey.kind === 'deep' && !journey.parentJourneyId) {
      errors.push(`Deep journey ${journey.id} missing parentJourneyId`);
    }
    if (journey.kind === 'surface' && !journey.surface) {
      errors.push(`Surface journey ${journey.id} missing surface`);
    }
    for (const step of journey.steps) {
      const target = TARGET_BY_ID.get(step.targetId);
      if (!target) {
        errors.push(
          `Journey ${journey.id} step ${step.id} missing target ${step.targetId}`,
        );
      }
      if (
        journey.kind === 'surface' &&
        journey.surface &&
        target &&
        target.surface !== 'chrome' &&
        target.surface !== journey.surface
      ) {
        errors.push(
          `Surface journey ${journey.id} step ${step.id} targets incompatible surface ${target.surface}`,
        );
      }
      if (
        step.required &&
        target &&
        (target.catalogOnly || target.contextualWhenMounted)
      ) {
        errors.push(
          `Required step ${step.id} targets non-guaranteed target ${target.id}`,
        );
      }
      for (const action of step.prepare ?? []) {
        if (!knownActions.has(action.kind)) {
          errors.push(
            `Journey ${journey.id} step ${step.id} has unknown action ${action.kind}`,
          );
        }
      }
      if (step.deepDiveJourneyId && !JOURNEY_BY_ID.has(step.deepDiveJourneyId)) {
        errors.push(
          `Step ${step.id} references missing deep dive ${step.deepDiveJourneyId}`,
        );
      }
    }
  }
  for (const journey of FTUE_JOURNEYS) {
    if (
      journey.parentJourneyId &&
      !JOURNEY_BY_ID.has(journey.parentJourneyId)
    ) {
      errors.push(
        `Journey ${journey.id} has missing parent ${journey.parentJourneyId}`,
      );
    }
  }

  const full = getFtueJourney('full');
  if (!full || full.steps.length < 5 || full.steps.length > 8) {
    errors.push(
      `Full tour should be 5–8 steps, got ${full?.steps.length ?? 0}`,
    );
  }
  return errors;
}
